import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { AggregatedEvent } from '../../types/calendarTypes';
import { getStatusColor } from '../../utils/calendarUtils';
import { formatSkillCodes } from '../../utils/skillUtils';

interface CalendarEventComponentProps {
    event: AggregatedEvent;
    /** Skill code -> long-form description lookup. */
    skillLabelByCd?: Record<string, string>;
    variant?: 'compact' | 'detailed';
    /** The active calendar view - used to decide how much detail fits. */
    calendarView?: 'month' | 'week' | 'day' | string;
}

const getTotalHours = (event: AggregatedEvent): number =>
    event.resource.events.reduce(
        (sum, e) => sum + (typeof e.requestHours === 'number' ? e.requestHours : 0),
        0
    );

const getSkillLabel = (event: AggregatedEvent, skillLabelByCd?: Record<string, string>): string => {
    const raw = event.resource.skillCd || event.resource.skillDesc || event.resource.skill;
    if (!raw) return '';
    return (skillLabelByCd?.[raw]) || formatSkillCodes(raw);
};

const CalendarEventComponent: React.FC<CalendarEventComponentProps> = ({ event, skillLabelByCd, variant = 'compact', calendarView }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const resource = event.resource;

    const skillLabel = getSkillLabel(event, skillLabelByCd);
    const totalHours = getTotalHours(event);
    const claimedByNames = Array.from(new Set(
        resource.events
            .map((e) => e.claimedByName)
            .filter((name): name is string => !!name)
    ));
    const peopleLabel = claimedByNames.length > 0
        ? claimedByNames.join(', ')
        : resource.postedByUserNm;

    // Only the month grid is narrow enough on mobile to warrant the
    // ultra-compact summary - week/day views render one event per full-width
    // row, so there's room to show the same level of detail as desktop.
    const useCompactMobileLayout = variant === 'detailed' && isMobile && calendarView === 'month';

    if (useCompactMobileLayout) {
        return (
            <div className="rbc-custom-event">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                    <span className="rbc-custom-event-title" style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.68rem',
                        fontWeight: 600
                    }}>
                        {totalHours > 0 ? `${totalHours}h` : skillLabel || '—'}
                    </span>
                    {resource.count > 1 && (
                        <span style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '12px',
                            padding: '1px 6px',
                            fontSize: '0.58rem',
                            fontWeight: 700,
                            color: getStatusColor(resource.status)
                        }}>
                            {resource.count}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    if (variant === 'detailed') {
        const isMobileFullWidth = isMobile && calendarView !== 'month';
        return (
            <div className="rbc-custom-event">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                    <span className="rbc-custom-event-title" style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobileFullWidth ? 'normal' : 'nowrap',
                        fontSize: isMobileFullWidth ? '0.95rem' : '0.72rem',
                        fontWeight: isMobileFullWidth ? 600 : 400,
                        lineHeight: 1.35
                    }}>
                        {event.title}
                    </span>
                    {resource.count > 1 && (
                        <span style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '12px',
                            padding: isMobileFullWidth ? '3px 10px' : '2px 8px',
                            fontSize: isMobileFullWidth ? '0.8rem' : '0.6rem',
                            fontWeight: 700,
                            color: getStatusColor(resource.status),
                            flexShrink: 0
                        }}>
                            {resource.count}
                        </span>
                    )}
                </div>
                <div className="rbc-custom-event-detail" style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: isMobileFullWidth ? 'normal' : 'nowrap',
                    fontSize: isMobileFullWidth ? '0.88rem' : '0.65rem',
                    lineHeight: 1.3,
                    marginTop: isMobileFullWidth ? 4 : 2
                }}>
                    {skillLabel || '—'}{totalHours > 0 ? ` • ${totalHours} Hours` : ''}
                </div>
                {peopleLabel && (
                    <div className="rbc-custom-event-detail" style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: isMobileFullWidth ? 'normal' : 'nowrap',
                        fontSize: isMobileFullWidth ? '0.88rem' : '0.62rem',
                        lineHeight: 1.3,
                        opacity: 0.85
                    }}>
                        {peopleLabel}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="rbc-custom-event">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="rbc-custom-event-title" style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem'
                }}>
                    {totalHours > 0 ? `${totalHours} Hours • ` : ''}{skillLabel || '—'}
                </span>
                {resource.count > 1 && (
                    <span style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        marginLeft: '4px',
                        color: getStatusColor(resource.status)
                    }}>
                        {resource.count}
                    </span>
                )}
            </div>
        </div>
    );
};

export default CalendarEventComponent;

